import moment from 'moment';
import { reportingClient, operatorClient } from 'domain/HttpClients';
import {
  getRoutes,
  getOrdersByCategoryData,
  getOrdersByCustomerData,
  getDatOrdersByCouriersData,
  getShippersSuccessRate,
  getOrderStatus,
  getDATOrderStatus,
  getOrdersTypesDonut,
  getWaybillsPerDay,
} from 'domain/tap/model';
import { LISTING_TRACKING_ENDPOINT } from 'domain/tap/constants';
import { formatMapResponse } from 'domain/tap/model';

const now = moment();
const startToday = now.startOf('day').toISOString();
const endToday = now.endOf('day').toISOString();

export const getCouriers = async (key) => {
  try {
    const response = await operatorClient
      .Get('api/accounts')
      .withParams({ name: key })
      .send();
    const output = response.results.map((row) => ({
      value: row.key,
      label: row.value.name,
    }));
    return output;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const getDrivers = async (key) => {
  try {
    const response = await operatorClient
      .Get('/api/drivers')
      .withParams({ firstName: key })
      .send();
    const output = response.results.map((row) => ({
      value: row.id,
      label: row.name,
      subLabel: row.companyCode,
    }));
    return output;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const getShippersPerformance = async (start, end, city, client, courier) => {
  try {
    const response = await reportingClient
      .Get('/api/v2/reports/waybill-by-vendor')
      .withParams({
        start,
        end,
        pickupCity: city,
        vendor: client,
        courier,
      })
      .send();

    return getShippersSuccessRate(response);
  } catch (e) {
    console.error(e);
    return [];
  }
};
export const getDriverActiveTrips = async (filters) => {
  let allActiveTrips = [];
  try {
    const response = await reportingClient
      .Get(LISTING_TRACKING_ENDPOINT)
      .withParams(filters)
      .send();
    if (response?.results) {
      allActiveTrips = [...allActiveTrips, ...response?.results];
    }

    let hasNext = response?.nextPage?.hasResults;
    let nextCursor = response?.nextPage?.cursor;
    while (hasNext) {
      const resp = await reportingClient
        .Get(LISTING_TRACKING_ENDPOINT)
        .withParams({ ...filters, cursor: nextCursor })
        .send();
      if (resp?.results) {
        allActiveTrips = [...allActiveTrips, ...resp?.results];
      }
      hasNext = resp?.nextPage?.hasResults;
      nextCursor = resp?.nextPage?.cursor;
    }
    return formatMapResponse({ results: allActiveTrips });
  } catch (e) {
    console.error(e);
    return allActiveTrips;
  }
};

export const getTrips = async (driverId, start, end, city, client, courier) => {
  try {
    let routes = [];
    let response = await reportingClient
      .Get('/api/v2/reports/drivers-active-trips')
      .withParams({
        driverId,
        start,
        end,
        pickupCity: city,
        vendor: client,
        courier,
      })
      .send();
    if (response?.results) {
      routes = [...response?.results];
    }
    let hasNext = response?.nextPage?.hasResults;
    let nextCursor = response?.nextPage?.cursor;
    while (hasNext) {
      response = await reportingClient
        .Get('/api/v2/reports/drivers-active-trips')
        .withParams({
          driverId,
          start,
          end,
          pickupCity: city,
          vendor: client,
          courier,
        })
        .send();
      if (response?.results) {
        routes = [...response?.results];
      }
      hasNext = resp?.nextPage?.hasResults;
      nextCursor = resp?.nextPage?.cursor;
    }

    return getRoutes(routes);
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const getShippers = async (key) => {
  try {
    const response = await operatorClient
      .Get('api/shippers')
      .withParams({ firstName: key, did: `${SYSTEM_DOMAIN}` })
      .send();
    const output = response.results.map((row) => ({
      value: row.id,
      label: row.name,
      subLabel: row.company,
    }));
    return output;
  } catch (e) {
    console.error(e);
    return [];
  }
};
export const getOrdersByCustomer = async (start, end, city, client, courier) => {
  try {
    const response = await reportingClient
      .Get('/api/v2/reports/waybill-by-customer')
      .withParams({
        start,
        end,
        pickupCity: city,
        vendor: client,
        courier,
      })
      .send();

    return getOrdersByCustomerData(response);
  } catch (e) {
    console.error(e);
    return [];
  }
};
export const getOrdersByCategory = async (start, end, city, client, courier) => {
  try {
    const response = await reportingClient
      .Get('/api/v2/reports/waybill-by-category')
      .withParams({
        start,
        end,
        pickupCity: city,
        vendor: client,
        courier,
      })
      .send();
    return getOrdersByCategoryData(response);
  } catch (e) {
    console.error(e);
    return [];
  }
};
export const getDatOrdersByCouriers = async (start, end, city, client, courier) => {
  try {
    const response = await reportingClient
      .Get('/api/v2/reports/waybill-by-courier')
      .withParams({
        start,
        end,
        pickupCity: city,
        vendor: client,
        courier,
      })
      .send();

    return getDatOrdersByCouriersData(response);
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const getOrders = async (start, end, city, client, courier) => {
  try {
    const response = await reportingClient
      .Get('/api/v2/reports/waybill-by-state')
      .withParams({
        start,
        end,
        pickupCity: city,
        vendor: client,
        courier,
      })
      .send();

    return getOrderStatus(response);
  } catch (e) {
    console.error(e);
    return getOrderStatus();
  }
};
export const getDATOrders = async (start, end, city, client, courier) => {
  try {
    const response = await reportingClient
      .Get('/api/v2/reports/sale-order-by-delivery-mode')
      .withParams({
        start,
        end,
        pickupCity: city,
        vendor: client,
        courier,
        deliveryMode: 'DAT',
      })
      .send();

    return getDATOrderStatus(response);
  } catch (e) {
    console.error(e);
    return getDATOrderStatus();
  }
};

export const getOrdersTypes = async (start, end, city, client, courier) => {
  try {
    const response = await reportingClient
      .Get('/api/v2/reports/sale-order-by-delivery-mode-summary')
      .withParams({
        start,
        end,
        pickupCity: city,
        vendor: client,
        courier,
      })
      .send();

    return getOrdersTypesDonut(response);
  } catch (e) {
    console.error(e);
    return getOrdersTypesDonut(null);
  }
};

export const getWaybillsPerDayChartData = async (start, end, city, client, courier) => {
  try {
    const response = await reportingClient
      .Get('/api/v2/reports/waybill-per-day')
      .withParams({
        start,
        end,
        pickupCity: city,
        vendor: client,
        courier,
      })
      .send();

    return getWaybillsPerDay(response);
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const getDropoffCities = async () => {
  try {
    const response = await operatorClient
      .Post(`/api/shipments/dropoff-cities?domain=${SYSTEM_DOMAIN}`)
      .send();
    return response;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const getPickupCities = async () => {
  try {
    const response = await operatorClient
      .Get(`/api/shipments/pickup-cities?domain=${SYSTEM_DOMAIN}`)
      .send();
    return response;
  } catch (e) {
    console.error(e);
    return [];
  }
};
